import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { auth } from '../../config/firebase';
import DashboardLayout from '../../components/layouts/dashboard/DashboardLayout';
import EditDialog from '../../components/layouts/dashboard/EditDialog';
import { colors, typography } from '../../styles/theme';
import axios from 'axios';
import { useOrganization } from '../../contexts/OrganizationContext';
import DataTable, { AmountCell } from '../../components/dashboard/tables/DataTable';

const TabWrapper = styled.div`
  margin-bottom: 24px;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 2px;
  padding: 4px;
  border-radius: 12px;
  width: fit-content;
  margin-bottom: 16px;
`;

const TabSeparator = styled.div`
  height: 1px;
  background: rgba(0, 0, 0, 0.1);
  width: 100%;
`;

const Tab = styled.button`
  padding: 8px 16px;
  border: none;
  background: ${props => props.active ? 'white' : 'transparent'};
  color: ${props => props.active ? colors.primary : colors.text.secondary};
  border-radius: 8px;
  cursor: pointer;
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.medium};
  line-height: 1.5;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.accent}10;
  }
`;

const ContentSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
`;

const SectionTitle = styled.h2`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.lg};
  color: ${colors.text.primary};
  margin: 0 0 24px 0;
  font-weight: ${typography.fontWeight.medium};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  border: 0px solid rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    background-color: rgba(0, 0, 0, 0.03);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  }
`;

const InfoLabel = styled.div`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.secondary};
  margin-bottom: 8px;
  font-weight: ${typography.fontWeight.medium};
`;

const InfoValue = styled.div`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.base};
  color: ${colors.text.primary};
  font-weight: ${typography.fontWeight.medium};
`;

const AddressCard = styled(InfoCard)`
  
  background: white;
`;

const AddressValue = styled(InfoValue)`
  font-size: ${typography.fontSize.base};
  line-height: 1.6;
`;

const InfoCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  color: ${colors.text.secondary};
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s ease;

  &:hover {
    color: ${colors.primary};
  }

  ${InfoCard}:hover & {
    opacity: 1;
  }
`;

const propertyColumns = [
  { key: 'id', label: 'Property ID', width: '20%' },
  { key: 'postcode', label: 'Postcode', width: '30%' },
  { key: 'rebuildValue', label: 'Rebuild Value', width: '30%' },
  { key: 'status', label: 'Status', width: '20%' }
];

const CompanyDetails = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialog, setEditDialog] = useState({ isOpen: false, field: null });
  const [orgId, setOrgId] = useState(null);
  const [properties, setProperties] = useState([]);
  
  const { organizationData, loading: contextLoading, error: contextError } = useOrganization();

  const tabs = [
    { id: 'overview', label: 'Company Overview' },
    { id: 'insurance', label: 'Insurance Details' },
    { id: 'property', label: 'Property Portfolio' },
    { id: 'contact', label: 'Contact Information' },
    { id: 'compliance', label: 'Compliance & Documentation' },
  ];

  useEffect(() => {
    // If context data is available and valid, use it
    if (organizationData && !contextError) {
      setCompanyData(organizationData);
      setOrgId(organizationData.id);
      setLoading(false);
      setError(null);
      return;
    }

    // Fallback to direct API call if context fails or isn't ready
    const fetchUserOrganization = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error('No authenticated user found');
        }

        const userResponse = await axios.get(`/api/dashboard/user/${currentUser.uid}`);
        const organizationId = userResponse.data.organizationId;
        
        if (!organizationId) {
          throw new Error('No organization found for user');
        }

        setOrgId(organizationId);
        
        const response = await axios.get(`/api/dashboard/company/${organizationId}`, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Expires': '0',
          }
        });
        
        setCompanyData(response.data.data);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to fetch company data');
        console.error('Error fetching company data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserOrganization();
  }, [organizationData, contextError]);

  useEffect(() => {
    const fetchPropertyData = async () => {
      try {
        if (!orgId) {
          console.log('No orgId available for property fetch');
          return;
        }
        
        console.log('Fetching property data for organization:', orgId);
        const response = await axios.get(`/api/dashboard/property/${orgId}`);
        console.log('Property data response:', response.data);
        
        if (response.data.success) {
          setProperties(response.data.data.properties);
          console.log('Set properties to:', response.data.data.properties);
        } else {
          console.log('Property fetch not successful:', response.data);
        }
      } catch (err) {
        console.error('Error fetching property data:', err.response || err);
      }
    };

    if (organizationData && !contextError) {
      fetchPropertyData();
    }
  }, [organizationData, contextError, orgId]);

  const formatAddress = (address) => {
    if (!address) return [];
    return address.split(',').map(line => line.trim());
  };

  const handleEdit = (field, value, label) => {
    console.log('Opening edit dialog with:', { field, value, label });
    setEditDialog({
      isOpen: true,
      field,
      value,
      label
    });
  };

  const handleSave = async (newValue) => {
    try {
      if (!orgId) {
        throw new Error('No organization ID available');
      }
      
      const response = await axios.put(`/api/dashboard/company/${orgId}/field`, {
        field: editDialog.field,
        value: newValue
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to update field');
      }

      // Update local state
      setCompanyData(prev => ({
        ...prev,
        [editDialog.field]: newValue
      }));
    } catch (err) {
      console.error('Error saving data:', err);
      // You might want to show an error message to the user here
    }
  };

  const renderInfoCard = (label, value) => (
    <InfoCard>
      <InfoLabel>{label}</InfoLabel>
      <InfoValue>{value || 'Not specified'}</InfoValue>
    </InfoCard>
  );

  const renderPropertyCell = (key, value, row) => {
    switch (key) {
      case 'rebuildValue':
        return <AmountCell value={value} />;
      default:
        return value || '-';
    }
  };

  const renderContent = () => {
    if (loading) {
      return <div>Loading...</div>;
    }

    if (error) {
      return <div>Error: {error}</div>;
    }

    if (!companyData) {
      return <div>No company data available</div>;
    }

    console.log('Rendering company data:', {
      name: companyData.name,
      number: companyData.number,
      type: companyData.type,
      incorporationDate: companyData.incorporationDate,
      address: companyData.registeredAddress,
      turnover: companyData.turnover,
      employees: companyData.employeeCount,
      contractors: companyData.contractorCount
    });

    switch (activeTab) {
      case 'overview':
        return (
          <>
            <ContentSection>
              <SectionTitle>Basic Information</SectionTitle>
              <Grid>
                {renderInfoCard('Company Name', companyData.name)}
                {renderInfoCard('Company Number', companyData.number)}
                {renderInfoCard('Organisation Type', companyData.type)}
                {renderInfoCard('Incorporation Date', companyData.incorporationDate)}
                <AddressCard>
                  <InfoLabel>Registered Address</InfoLabel>
                  <AddressValue>
                    {formatAddress(companyData.registeredAddress).map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </AddressValue>
                </AddressCard>
                <InfoCard>
                  <InfoCardHeader>
                    <InfoLabel>Industry</InfoLabel>
                    <EditButton onClick={() => handleEdit('industry', companyData.industry, 'Industry')}>✎</EditButton>
                  </InfoCardHeader>
                  <div>
                    <InfoValue>{companyData.industry || 'Not specified'}</InfoValue>
                  </div>
                  {companyData.subIndustry && (
                    <div style={{ marginTop: '30px' }}>
                      <InfoCardHeader>
                        <InfoLabel>Sub-Industry</InfoLabel>
                        <EditButton onClick={() => handleEdit('subIndustry', companyData.subIndustry, 'Sub-Industry')}>✎</EditButton>
                      </InfoCardHeader>
                      <InfoValue>{companyData.subIndustry}</InfoValue>
                    </div>
                  )}
                </InfoCard>
              </Grid>
            </ContentSection>

            <ContentSection>
              <SectionTitle>Business Metrics</SectionTitle>
              <Grid>
                <InfoCard>
                  <InfoCardHeader>
                    <InfoLabel>Annual Turnover</InfoLabel>
                    <EditButton onClick={() => handleEdit('turnover', companyData.turnover, 'Annual Turnover')}>✎</EditButton>
                  </InfoCardHeader>
                  <InfoValue>{`£${parseInt(companyData.turnover || 0).toLocaleString()}`}</InfoValue>
                </InfoCard>
                <InfoCard>
                  <InfoCardHeader>
                    <InfoLabel>Employee Count</InfoLabel>
                    <EditButton onClick={() => handleEdit('employeeCount', companyData.employeeCount, 'Employee Count')}>✎</EditButton>
                  </InfoCardHeader>
                  <InfoValue>{companyData.employeeCount || 'Not specified'}</InfoValue>
                </InfoCard>
                <InfoCard>
                  <InfoCardHeader>
                    <InfoLabel>Contractor Count</InfoLabel>
                    <EditButton onClick={() => handleEdit('contractorCount', companyData.contractorCount, 'Contractor Count')}>✎</EditButton>
                  </InfoCardHeader>
                  <InfoValue>{companyData.contractorCount || 'Not specified'}</InfoValue>
                </InfoCard>
              </Grid>
            </ContentSection>
          </>
        );
      case 'property':
        return (
          <DataTable 
            title="Property Portfolio"
            columns={propertyColumns}
            data={properties}
            renderCell={renderPropertyCell}
            emptyState="No properties in portfolio"
          />
        );
      // Add other tab content cases here
      default:
        return null;
    }
  };

  return (
    <DashboardLayout title="Company Details" currentPath="/dashboard/company">
      <TabWrapper>
        <TabContainer>
          {tabs.map(tab => (
            <Tab
              key={tab.id}
              active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </Tab>
          ))}
        </TabContainer>
        <TabSeparator />
      </TabWrapper>
      {renderContent()}
      <EditDialog
        isOpen={editDialog.isOpen}
        onClose={() => setEditDialog({ isOpen: false, field: null })}
        onSave={handleSave}
        title={`Edit ${editDialog.label}`}
        fieldLabel={editDialog.label}
        initialValue={editDialog.value}
        field={editDialog.field}
      />
    </DashboardLayout>
  );
};

export default CompanyDetails; 